export type Oklab = [number, number, number];

interface OklchColor {
  lightness: number;
  chroma: number;
  hue: number;
}

export interface OklchHueAndChroma {
  hue: number;
  chroma: number;
}

const HEX_COLOR = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const OKLCH_COLOR =
  /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+(-?[\d.]+(?:deg)?|none)\s*\)$/i;
const DEGREE_UNIT = /deg$/i;
const TRAILING_SEMICOLON = /;$/;
const FULL_TURN_DEGREES = 360;

export function normalizeHexColor(color: string): string | undefined {
  const digits = color.trim().match(HEX_COLOR)?.[1].toLowerCase();

  if (digits === undefined) {
    return undefined;
  }
  if (digits.length === 3) {
    return `#${[...digits].map((digit) => `${digit}${digit}`).join('')}`;
  }

  return `#${digits}`;
}

function parseOklchColor(color: string): OklchColor | undefined {
  const parts = color.trim().match(OKLCH_COLOR);

  if (!parts) {
    return undefined;
  }

  const [, lightness, percent, chroma, hue] = parts;

  return {
    lightness: Number(lightness) / (percent ? 100 : 1),
    chroma: Number(chroma),
    hue:
      hue.toLowerCase() === 'none' ? 0 : Number(hue.replace(DEGREE_UNIT, '')),
  };
}

const toLinearChannel = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

const toGammaChannel = (channel: number): number =>
  channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055;

const toHexChannel = (channel: number): string =>
  Math.round(Math.min(Math.max(toGammaChannel(channel), 0), 1) * 255)
    .toString(16)
    .padStart(2, '0');

// shortcut: out-of-gamut colors are clipped per channel, not gamut-mapped, upgrade if the browser stops clipping
function oklchToHex({ lightness, chroma, hue }: OklchColor): string {
  const radians = (hue * Math.PI) / 180;
  const greenRed = chroma * Math.cos(radians);
  const blueYellow = chroma * Math.sin(radians);
  const long =
    (lightness + 0.3963377774 * greenRed + 0.2158037573 * blueYellow) ** 3;
  const medium =
    (lightness - 0.1055613458 * greenRed - 0.0638541728 * blueYellow) ** 3;
  const short =
    (lightness - 0.0894841775 * greenRed - 1.291485548 * blueYellow) ** 3;

  return `#${[
    4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short,
    -1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
    -0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short,
  ]
    .map(toHexChannel)
    .join('')}`;
}

export function toHexColor(color: string): string | undefined {
  const cleaned = color.trim().replace(TRAILING_SEMICOLON, '');
  const oklch = parseOklchColor(cleaned);

  return oklch ? oklchToHex(oklch) : normalizeHexColor(cleaned);
}

export function hexToOklab(hex: string): Oklab {
  const [red, green, blue] = [1, 3, 5].map((start) =>
    toLinearChannel(Number.parseInt(hex.slice(start, start + 2), 16) / 255),
  );
  const long = Math.cbrt(
    0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue,
  );
  const medium = Math.cbrt(
    0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue,
  );
  const short = Math.cbrt(
    0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue,
  );

  return [
    0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
    1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short,
    0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short,
  ];
}

export function toOklchHueAndChroma(
  color: string,
): OklchHueAndChroma | undefined {
  const hex = toHexColor(color);

  if (!hex) {
    return undefined;
  }

  const [, greenRed, blueYellow] = hexToOklab(hex);
  const degrees = (Math.atan2(blueYellow, greenRed) * 180) / Math.PI;

  return {
    hue: (degrees + FULL_TURN_DEGREES) % FULL_TURN_DEGREES,
    chroma: Math.hypot(greenRed, blueYellow),
  };
}
